import {
  CORE_PLAN_FEATURE_KEYS,
  LICENSE_AI_CREDITS_OPTIONS,
  LICENSE_DURATION_OPTIONS,
  LICENSE_USER_OPTIONS,
} from './licenseConstants';

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function slugifyCode(value = '') {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function getDurationOption(months) {
  return LICENSE_DURATION_OPTIONS.find((option) => option.value === months) ?? null;
}

export function resolveDurationFromMonths(months) {
  const preset = getDurationOption(months);

  if (preset) {
    return {
      durationMonths: preset.value,
      billingInterval: preset.billingInterval,
      duration: preset.duration,
    };
  }

  const parsed = Number(months);
  return {
    durationMonths: parsed,
    billingInterval: 'monthly',
    duration: parsed,
  };
}

export function getDurationLabel(months) {
  const match = getDurationOption(months);
  return match?.label ?? `${months} Months`;
}

export function getUsersLabel(value) {
  const match = LICENSE_USER_OPTIONS.find((option) => option.value === value);
  if (match) return match.label;
  if (value === -1) return 'Unlimited';
  if (value === null || value === undefined) return '—';
  return `${Number(value).toLocaleString('en-IN')} Users`;
}

export function getAiCreditsLabel(value) {
  const match = LICENSE_AI_CREDITS_OPTIONS.find((option) => option.value === value);
  if (match) return match.label;
  if (value === -1) return 'Unlimited';
  if (value === null || value === undefined) return '—';
  return `${Number(value).toLocaleString('en-IN')} Credits`;
}

export function getFeatureLimit(featureSummary = [], featureKey) {
  const match = featureSummary.find((feature) => feature.key === featureKey);
  if (!match || !match.enabled) return null;
  return toNumber(match.hard_limit ?? match.hardLimit);
}

export function getBillingIntervalLabel(interval) {
  if (!interval) return 'plan';
  return interval.replace(/_/g, ' ');
}

export function formatPlanPrice(price, durationMonths, billingInterval) {
  if (price === '' || price === null || price === undefined) {
    return 'Price not set';
  }

  const durationLabel = durationMonths
    ? getDurationLabel(durationMonths)
    : getBillingIntervalLabel(billingInterval);

  return `₹${Number(price).toLocaleString('en-IN')} / ${durationLabel}`;
}

function mapDurationFromBackend(duration, billingInterval) {
  const durationValue = toNumber(duration) ?? 1;
  const interval = billingInterval || 'monthly';

  if (interval === 'yearly') {
    if (durationValue === 1) return 12;
    if (durationValue === 2) return 24;
    return durationValue * 12;
  }

  return durationValue;
}

function buildFeatureSummaryFromForm(form) {
  const features = [];

  if (form.maxUsers !== null && form.maxUsers !== undefined && form.maxUsers !== '') {
    features.push({
      feature_key: CORE_PLAN_FEATURE_KEYS.users,
      enabled: true,
      hard_limit: form.maxUsers,
    });
  }

  if (form.aiCredits !== null && form.aiCredits !== undefined && form.aiCredits !== '') {
    features.push({
      feature_key: CORE_PLAN_FEATURE_KEYS.aiCredits,
      enabled: true,
      hard_limit: form.aiCredits,
    });
  }

  return features;
}

export function normalizeLicense(raw = {}) {
  const featureSummary = raw.feature_summary ?? raw.featureSummary ?? [];
  const permissionProfile = raw.permission_profile ?? raw.permissionProfile ?? null;
  const billingInterval = raw.billing_interval ?? raw.billingInterval ?? 'monthly';
  const duration = toNumber(raw.duration) ?? 1;
  const durationMonths = mapDurationFromBackend(duration, billingInterval);
  const maxUsers = getFeatureLimit(featureSummary, CORE_PLAN_FEATURE_KEYS.users);
  const aiCredits = getFeatureLimit(featureSummary, CORE_PLAN_FEATURE_KEYS.aiCredits);

  return {
    id: raw.id,
    code: raw.code ?? '',
    name: raw.name ?? '',
    alias: raw.alias ?? raw.display_name ?? raw.displayName ?? '',
    description: raw.description ?? '',
    status: raw.status ?? 'active',
    billingInterval,
    duration,
    durationMonths,
    currency: raw.currency ?? 'INR',
    trialDays: toNumber(raw.trial_days ?? raw.trialDays) ?? 0,
    price: raw.price ?? raw.plan_price ?? raw.pricePerMonth ?? raw.price_per_month ?? '',
    permissionProfile,
    permissionProfileId: permissionProfile?.id ?? raw.permission_profile_id ?? null,
    featureSummary: Array.isArray(featureSummary) ? featureSummary : [],
    maxUsers,
    aiCredits,
    createdAt: raw.created_at ?? raw.createdAt ?? null,
  };
}

export function normalizeLicenseList(rawList = []) {
  return rawList.map(normalizeLicense);
}

export function normalizeProfileList(rawList = []) {
  return rawList.map((profile) => ({
    id: profile.id,
    name: profile.name,
    organisationId: profile.organisation_id ?? profile.organisationId ?? null,
    organisationName: profile.organisation_name ?? profile.organisationName ?? '',
  }));
}

const ACTIVE_FEATURE_KEYS = Object.values(CORE_PLAN_FEATURE_KEYS);

export function normalizeFeatureList(rawList = []) {
  return rawList
    .map((feature) => ({
      id: feature.id,
      key: feature.key,
      name: feature.name,
      category: feature.category,
      valueType: feature.value_type ?? feature.valueType,
      metered: Boolean(feature.metered),
      resetPeriod: feature.reset_period ?? feature.resetPeriod,
      description: feature.description ?? '',
    }))
    .filter((feature) => ACTIVE_FEATURE_KEYS.includes(feature.key));
}

export function licenseToFormValues(license = {}) {
  return {
    name: license.name ?? '',
    alias: license.alias ?? '',
    status: license.status ?? 'active',
    description: license.description ?? '',
    durationMonths: license.durationMonths ?? null,
    billingInterval: license.billingInterval ?? 'monthly',
    duration: license.duration ?? 1,
    currency: license.currency ?? 'INR',
    trialDays: license.trialDays ?? 0,
    maxUsers: license.maxUsers ?? null,
    aiCredits: license.aiCredits ?? null,
    price: license.price ?? '',
    permissionProfileId: license.permissionProfileId ?? license.permissionProfile?.id ?? null,
  };
}

function buildLicensePayload(form, { includeCode = false } = {}) {
  const durationOption = resolveDurationFromMonths(form.durationMonths);

  const payload = {
    name: form.name.trim(),
    alias: form.alias.trim(),
    status: form.status,
    description: (form.description || '').trim(),
    billing_interval: durationOption.billingInterval,
    duration: durationOption.duration,
    currency: form.currency || 'INR',
    price: Number(form.price),
    trial_days: Number(form.trialDays || 0),
    permission_profile_id: form.permissionProfileId,
    features: buildFeatureSummaryFromForm(form),
  };

  if (includeCode) {
    payload.code = slugifyCode(form.alias || form.name);
  }

  return payload;
}

export function buildCreateLicensePayload(form) {
  return buildLicensePayload(form, { includeCode: true });
}

export function buildUpdateLicensePayload(form) {
  return buildLicensePayload(form, { includeCode: false });
}
